import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Country waitlist.
 *
 * Port of `src/app/admin/waitlist/page.tsx` (legacy NextJS, ~250 LOC) per D21.
 * Per-country signups by role with collaborator approval flow.
 *
 * Endpoints:
 *   - GET  /api/admin/country-waitlist                → counts + total
 *   - GET  /api/admin/country-waitlist?country=XX    → entries
 *   - POST /api/admin/country-waitlist/approve { entryId }
 */

interface WaitlistEntry {
  id: string;
  countryCode: string;
  role: string;
  name: string;
  email: string;
  locale: string;
  createdAt: string;
  notifiedAt: string | null;
}

interface WaitlistCounts {
  [countryCode: string]: {
    user: number;
    provider: number;
    clinic: number;
    collaborator: number;
    total: number;
  };
}

const ROLE_ICONS: Record<string, string> = {
  user: "👤",
  provider: "🩺",
  clinic: "🏥",
  collaborator: "🤝",
};
const ROLE_COLORS: Record<string, string> = {
  user: "bg-blue-100 text-blue-700",
  provider: "bg-emerald-100 text-emerald-700",
  clinic: "bg-purple-100 text-purple-700",
  collaborator: "bg-amber-100 text-amber-700",
};

export const Route = createFileRoute("/_authenticated/waitlist")({
  component: WaitlistPage,
});

function WaitlistPage() {
  const { t } = useLingui();
  const { visibleCountries: countries } = useAdminCountries();
  const { dbTarget } = useAdminContext();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [waitlistCounts, setWaitlistCounts] = useState<WaitlistCounts>({});
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch
      .get<{ counts: WaitlistCounts }>("/api/admin/country-waitlist")
      .then((data) => setWaitlistCounts(data.counts || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dbTarget]);

  const refreshEntries = useCallback(() => {
    if (!selectedCountry) return;
    apiFetch
      .get<{ entries: WaitlistEntry[] }>(`/api/admin/country-waitlist?country=${selectedCountry}`)
      .then((data) => setWaitlistEntries(data.entries || []))
      .catch(() => {});
    // dbTarget reactively re-runs via the useEffect dep below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, dbTarget]);

  useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  const handleApprove = async (entryId: string) => {
    setApproving(entryId);
    try {
      await apiFetch.post("/api/admin/country-waitlist/approve", { entryId });
      refreshEntries();
    } finally {
      setApproving(null);
    }
  };

  const handleCountrySelect = (code: string) => {
    setSelectedCountry(code);
    if (!code) setWaitlistEntries([]);
  };

  const pillCounts: Record<string, number> = {};
  for (const [code, counts] of Object.entries(waitlistCounts)) {
    pillCounts[code] = counts.total;
  }

  const totalWaitlist = Object.values(waitlistCounts).reduce((s, c) => s + c.total, 0);
  const totalProviders = Object.values(waitlistCounts).reduce((s, c) => s + c.provider, 0);
  const totalClinics = Object.values(waitlistCounts).reduce((s, c) => s + c.clinic, 0);
  const totalUsers = Object.values(waitlistCounts).reduce((s, c) => s + c.user, 0);
  const totalCollabs = Object.values(waitlistCounts).reduce((s, c) => s + (c.collaborator || 0), 0);

  const stats = [
    { label: t`Total waitlist`, value: totalWaitlist, icon: "📋", color: "text-blue-600" },
    { label: t`Users`, value: totalUsers, icon: "👤", color: "text-sky-600" },
    { label: t`Providers`, value: totalProviders, icon: "🩺", color: "text-emerald-600" },
    { label: t`Companies`, value: totalClinics, icon: "🏥", color: "text-purple-600" },
    { label: t`Collaborators`, value: totalCollabs, icon: "🤝", color: "text-amber-600" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Country waitlist</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Per-country signups before launch</Trans>
        </p>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={pillCounts}
        selected={selectedCountry}
        onSelect={handleCountrySelect}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <span className="text-2xl">{stat.icon}</span>
              <div className={`mt-2 text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-muted-foreground text-xs">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Entries</Trans>
            {selectedCountry && (
              <span className="text-muted-foreground ml-2 text-sm font-normal">
                — {countries.find((c) => c.code === selectedCountry)?.name?.en || selectedCountry}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>Loading...</Trans>
            </div>
          ) : totalWaitlist === 0 ? (
            <div className="border-border/50 rounded-lg border border-dashed p-8 text-center">
              <span className="text-4xl">📋</span>
              <p className="text-muted-foreground mt-3 text-sm">
                <Trans>No entries</Trans>
              </p>
            </div>
          ) : !selectedCountry ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>Select a country</Trans>
            </div>
          ) : waitlistEntries.length === 0 ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>No entries for this country</Trans>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border/50 border-b text-left">
                    <th className="px-3 py-2 font-medium">
                      <Trans>Name</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Email</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Role</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Language</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Date</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Notified</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Actions</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {waitlistEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-border/30 hover:bg-muted/30 border-b transition-colors"
                    >
                      <td className="px-3 py-2.5 font-medium">{entry.name}</td>
                      <td className="text-muted-foreground px-3 py-2.5">{entry.email}</td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                            ROLE_COLORS[entry.role] || ""
                          }`}
                        >
                          {ROLE_ICONS[entry.role] || ""} {entry.role}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 uppercase">{entry.locale}</td>
                      <td className="text-muted-foreground px-3 py-2.5 text-xs">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2.5">
                        {entry.notifiedAt ? (
                          <Badge variant="outline" className="text-[0.625rem] text-emerald-600">
                            ✓ {new Date(entry.notifiedAt).toLocaleDateString()}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {entry.role === "collaborator" && !entry.notifiedAt && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={approving === entry.id}
                            onClick={() => handleApprove(entry.id)}
                          >
                            {approving === entry.id ? "..." : <Trans>Approve collaborator</Trans>}
                          </Button>
                        )}
                        {entry.role === "collaborator" && entry.notifiedAt && (
                          <Badge variant="outline" className="text-[0.625rem] text-emerald-600">
                            ✓ <Trans>Approved</Trans>
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
